/**
 * Shared helpers dla R1-R7 rules. Pure functions, zero I/O.
 *
 * tokenize + jaccardSimilarity inlined (mirror z src/services/deduplicationService.ts:129-139)
 * żeby rules nie importowały całego dedup stack'u (który ma chain do supabase/openaiProvider).
 * Smoke test (tsx) potrzebuje uruchamiać rules bez ładowania Vite-only kodu.
 */

import type { RepairNodeRow, RepairSeedRow } from "@/types/mapRepair";

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-ząćęłńóśźż0-9\s]/g, "")
    .split(/\s+/)
    .filter((w) => w.length > 2);
}

export function jaccardSimilarity(a: string[], b: string[]): number {
  const setA = new Set(a);
  const setB = new Set(b);
  const intersection = [...setA].filter((x) => setB.has(x)).length;
  const union = new Set([...setA, ...setB]).size;
  return union === 0 ? 0 : intersection / union;
}

/**
 * Tokeny dla noda: entity_raw ∪ slug (z zamianą myślników na spacje).
 * Te same tokeny są używane wszędzie (R2 target selection, R3 misplacement, R6 orphan link).
 */
export function nodeTokens(node: RepairNodeRow): string[] {
  const entityTokens = tokenize(node.primary_entity_raw || "");
  const slugText = node.slug.replace(/-/g, " ");
  const slugTokens = tokenize(slugText);
  return Array.from(new Set([...entityTokens, ...slugTokens]));
}

/**
 * Tokeny dla seeda: name + concat(primary_entity_raw wszystkich nodów seeda).
 * Używane jako "scope" seeda do porównania z nodem.
 */
export function seedTokens(
  seed: RepairSeedRow,
  nodesInSeed: RepairNodeRow[],
): string[] {
  const nameTokens = tokenize(seed.name);
  const entitiesText = nodesInSeed
    .map((n) => n.primary_entity_raw || "")
    .filter(Boolean)
    .join(" ");
  const entityTokens = tokenize(entitiesText);
  return Array.from(new Set([...nameTokens, ...entityTokens]));
}

/**
 * Canonical selection (R1/R2 tie-break kolejno):
 *  1. MAX(search_volume)
 *  2. MAX(incoming_count)
 *  3. MIN(LENGTH(slug))
 *  4. slug ASC (alfabetycznie)
 *
 * Stability hint: dla R2 orchestrator może podać `preferredSeedId` — node z tego
 * seeda wygrywa jeśli istnieje (user-expectations: node który już jest w target
 * nie "migruje" tylko stoi w miejscu).
 */
export function pickCanonical(
  nodes: RepairNodeRow[],
  incomingByNodeId: Map<string, number>,
  opts: { preferredSeedId?: string } = {},
): RepairNodeRow {
  if (nodes.length === 0) {
    throw new Error("pickCanonical: empty nodes list");
  }
  if (opts.preferredSeedId) {
    const inTarget = nodes.filter((n) => n.seed_id === opts.preferredSeedId);
    if (inTarget.length === 1) return inTarget[0];
    if (inTarget.length > 1) return pickCanonical(inTarget, incomingByNodeId);
  }
  const scored = nodes.map((n) => ({
    node: n,
    sv: n.search_volume ?? 0,
    inc: incomingByNodeId.get(n.id) ?? 0,
    slugLen: n.slug.length,
  }));
  scored.sort((a, b) => {
    if (b.sv !== a.sv) return b.sv - a.sv; // MAX SV
    if (b.inc !== a.inc) return b.inc - a.inc; // MAX incoming
    if (a.slugLen !== b.slugLen) return a.slugLen - b.slugLen; // MIN slug len
    return a.node.slug.localeCompare(b.node.slug); // ASC slug
  });
  return scored[0].node;
}

/**
 * Group nodes by (seed_id, normalized_entity, normalized_intent).
 * Normalizacja: trim + lowercase. Zgodne z auditStructural.ts:165-227.
 */
export function groupBySeedEntityIntent(
  nodes: RepairNodeRow[],
): Map<string, RepairNodeRow[]> {
  const groups = new Map<string, RepairNodeRow[]>();
  for (const n of nodes) {
    const entity = (n.primary_entity_raw || "").trim().toLowerCase();
    const intent = (n.primary_intent || "").trim().toLowerCase();
    if (!entity || !intent) continue;
    const key = `${n.seed_id}|${entity}|${intent}`;
    const list = groups.get(key) || [];
    list.push(n);
    groups.set(key, list);
  }
  return groups;
}

/**
 * Group nodes by (normalized_entity, normalized_intent) ACROSS seeds (R2).
 */
export function groupByEntityIntentCrossSeeds(
  nodes: RepairNodeRow[],
): Map<string, RepairNodeRow[]> {
  const groups = new Map<string, RepairNodeRow[]>();
  for (const n of nodes) {
    const entity = (n.primary_entity_raw || "").trim().toLowerCase();
    const intent = (n.primary_intent || "").trim().toLowerCase();
    if (!entity || !intent) continue;
    const key = `${entity}|${intent}`;
    const list = groups.get(key) || [];
    list.push(n);
    groups.set(key, list);
  }
  return groups;
}

